import { type ChangeEvent, type FC, useState } from "react";
import Modal from "~/common/components/Modal";
import { LinearPopupState } from "~/features/configurator/Linear/constants";
import { getCurrentStep, getProductCode } from "~/features/configurator/Linear/redux/selectors";
import { useDispatch, useSelector } from "react-redux";
import { Button, Stack, TextField, Typography } from "~/common/mui";
import { CloseBtn } from "~/features/addToCart/components/AtcCloseButton";
import { Row } from "~/common/components/Layout";
import { sendLinearRequestQuote } from "~/features/configurator/Linear/api/sendLinearRequestQuote";
import { setAlreadyAddedToCart } from "~/features/configurator/Linear/redux/actions";
import { useTranslation } from "react-i18next";
import { useLinearProductInfo } from "~/features/configurator/Linear/redux/useLinearProductInfo";
import { Alert } from "~/common/mui/Alert/Alert";
import { useSession } from "~/app/providers/SessionProvider";

interface Props {
  onClose: () => void;
}

const LinearRequestQuotePopup: FC<Props> = (props: Props) => {
  const { onClose } = props;
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const requestQuoteEmail = useSession()?.commerceContext?.linearConfiguratorEmail ?? "";

  const [state, setState] = useState(LinearPopupState.INITIAL);
  const [recipientNote, setRecipientNote] = useState<string>("");
  const updateRecipientNote = (e: ChangeEvent<HTMLTextAreaElement>) => setRecipientNote(e.target.value);

  const productInfo = useLinearProductInfo();
  const productCode = useSelector(getProductCode);
  const currentStep = useSelector(getCurrentStep);

  const isInState = (...validStates: LinearPopupState[]) => {
    const result = validStates.find((el) => el === state);
    return result !== undefined;
  };

  const sendQuoteRequest = async () => {
    setState(LinearPopupState.SENDING);
    let success: boolean;
    try {
      success = await sendLinearRequestQuote(
        productCode,
        productInfo.configId,
        recipientNote,
        currentStep + 1,
        productInfo.designation,
      );
    } catch (e) {
      success = false;
    }
    if (success) {
      dispatch(setAlreadyAddedToCart(null));
    }
    setState(success ? LinearPopupState.SUCCESS : LinearPopupState.ERROR);
  };

  return (
    <Modal
      onClose={onClose}
      title={t("linearConfigSummary.card.addToQuote")}
      footerChildren={
        <Row className={"d-flex flex-justify-between flex-align-center w-100"}>
          <p>{t("linear.request.quote.popup.mandatory")}</p>
          <Stack direction={"row"} gap={1}>
            {isInState(LinearPopupState.INITIAL, LinearPopupState.SENDING) ? (
              <CloseBtn onClick={onClose} disabled={isInState(LinearPopupState.SENDING)}>
                {t("cancel")}
              </CloseBtn>
            ) : (
              <CloseBtn onClick={onClose}>{t("close")}</CloseBtn>
            )}

            <Button
              disabled={isInState(LinearPopupState.SENDING, LinearPopupState.ERROR)}
              onClick={isInState(LinearPopupState.INITIAL) ? sendQuoteRequest : undefined}
            >
              {t("linearConfigSummary.card.addToQuote")}
            </Button>
          </Stack>
        </Row>
      }
    >
      <div className={"mt-30"}>{t("linear.request.quote.popup.body")}</div>
      <Typography variant="bodySmall" sx={{ color: "text.label", mt: "30px" }}>
        {t("linear.request.quote.popup.email.label")}
      </Typography>
      <div className={"mt-1"}>{requestQuoteEmail}</div>
      <div className={"mt-2"}>
        <TextField
          variant={"outlined"}
          multiline
          fullWidth
          inputProps={{ sx: { height: "200px !important" } }}
          label={t("linear.request.quote.popup.input.label")}
          onChange={updateRecipientNote}
        />
      </div>
      <Alert className={"mt-10 mb-30"} severity="info">
        {t("linear.request.quote.popup.email.info")}
      </Alert>
    </Modal>
  );
};

export default LinearRequestQuotePopup;
